import { SizesProvider } from '@/state/sizesProvider';

import {
  EThemes,
  IImageGlobalCSProps,
  IImageProps,
} from '@/lib/contentstack-sdk/types';
import { parseHeaderFromCMS } from '@/lib/contentstack-sdk/utils/parseHelpers';

import {
  IHomePlanCMS,
  IHomePlanCMSSelect,
  IOrHomePlansCarouselCMSProps,
} from './or-home-plans-carousel.types';
import { OrHomePlansCarousel } from './or-home-plans-carousel';
import { convertCmsPlansIntoAlgoliaPlans } from './utils';

/** Type guards */
function isSelectItem(
  item: IHomePlanCMS | IHomePlanCMSSelect,
): item is IHomePlanCMSSelect {
  return 'home_plans' in item;
}

function isPlanItem(
  item: IHomePlanCMS | IHomePlanCMSSelect,
): item is IHomePlanCMS {
  return 'home_plan_id' in item;
}

export const OrHomePlansCarouselCMS = async ({
  heading,
  plans,
  aria_label_for_home_plan_like,
  top_spacing,
  bottom_spacing,
  theme,
  _content_type_uid: contentType,
}: IOrHomePlansCarouselCMSProps) => {
  const parsedHeading = heading?.length
    ? parseHeaderFromCMS(heading[0])
    : undefined;

  let planMetadata: {
    hpId: string;
    image: IImageGlobalCSProps | IImageProps;
    exteriorStyleId?: string;
  }[] = [];

  if (isSelectItem(plans[0])) {
    planMetadata = plans[0].home_plans
      .map((plan) => ({
        hpId: plan.hp?.select,
        image: plan.image,
        exteriorStyleId: plan.exterior_style?.style,
      }))
      .filter((p) => p.hpId && p.exteriorStyleId);
  } else if (isPlanItem(plans[0])) {
    // fallback for static CMS mode (legacy direct entry)
    planMetadata = plans.map((plan) => ({
      hpId: String((plan as IHomePlanCMS).home_plan_id),
      image: (plan as IHomePlanCMS).plan_image,
      exteriorStyleId: String(
        (plan as IHomePlanCMS).exterior_style?.exterior_name,
      ),
    }));
  }

  const fullPlans = await convertCmsPlansIntoAlgoliaPlans(
    planMetadata,
    undefined,
  );

  const props = {
    heading: parsedHeading,
    planMetadata,
    likePlanAriaLabel: aria_label_for_home_plan_like,
    theme: theme?.theme.toLowerCase() as EThemes,
    topSpacing: top_spacing?.spacing_value,
    topDivider: top_spacing?.divider,
    bottomSpacing: bottom_spacing?.spacing_value,
    bottomDivider: bottom_spacing?.divider,
    fullPlans,
  };

  return (
    <SizesProvider contentType={contentType}>
      <OrHomePlansCarousel {...props} />;
    </SizesProvider>
  );
};
